<script setup lang="ts">
import { computed, getCurrentInstance, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useDeviceAuthStore } from '@Stores/deviceAuth.store';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import useCompanyImageBaseUrl from '@Use/useCompanyImageBaseUrl';
import useFlowNavigation from '@Use/useFlowNavigation';
import { selectOtpAccountApi, selectNoMatchedAccountApi } from '@Api/otp.api';
import { handleStatusCode } from '@Utils/statusCodeHandler';
import type { AccountUserRow } from '@Types/deviceAuth.interface';

import { FlowLayout, ActionBarLayout } from '@Shared';

const { proxy } = getCurrentInstance();
const router = useRouter();
const deviceAuthStore = useDeviceAuthStore();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const companyImageBaseUrl = useCompanyImageBaseUrl();
const { advanceFlow } = useFlowNavigation();

const { matchedAccounts, error } = storeToRefs(deviceAuthStore);

// Composite key for radio selection: one row per (accountId, userId) pair.
const selectedKey = ref<string | null>(null);
const isSending = ref(false);
const isLoadingNoMatchSelection = ref(false);

const rowKey = (row: AccountUserRow) => `${row.accountId}:${row.userId}`;

const displayName = (row: AccountUserRow) => {
    const full = [row.firstName, row.lastName].filter(Boolean).join(' ').trim();
    return full || row.email;
};

const selectedRow = computed<AccountUserRow | null>(() => {
    if (!selectedKey.value) return null;
    return matchedAccounts.value?.find(r => rowKey(r) === selectedKey.value) ?? null;
});

// Clear the stale "no longer valid" error as soon as the user picks any
// account row. Otherwise the InvalidAccountSelection error from the previous
// attempt sticks around until they click Continue, which contradicts the
// expected interaction (re-picking should reset the error).
watch(selectedKey, (newVal) => {
    if (newVal !== null) {
        deviceAuthStore.setError('');
    }
});

// Surface any error the statusCodeHandler put on the store (e.g., after
// InvalidAccountSelection bounced us back to the picker).
const inlineError = computed(() => error.value);

const selectAccount = async () => {
    const row = selectedRow.value;
    if (!row) return;

    // Clear any stale error from a previous InvalidAccountSelection bounce so
    // the user doesn't see the old message after re-picking.
    deviceAuthStore.setError('');

    isSending.value = true;
    try {
        const request = { accountId: row.accountId, userId: row.userId };
        const response = await selectOtpAccountApi(request);

        await handleStatusCode(response, router, advanceFlow);
    } catch (err) {
        proxy.$apptoast('An error occurred. Please try again.');
        console.error('selectOtpAccountApi error:', err);
    } finally {
        isSending.value = false;
    }
};

const selectNoMatchedAccount = async () => {
    isLoadingNoMatchSelection.value = true;
    try {
        const response = await selectNoMatchedAccountApi();
        await handleStatusCode(response, router, advanceFlow);
    } catch (err) {
        proxy.$apptoast('An error occurred. Please try again.');
        console.error('selectNoMatchedAccountApi error:', err);
    } finally {
        isLoadingNoMatchSelection.value = false;
    }
};
</script>

<template>
    <flow-layout
        suppress-back-to-dashboard
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl">

        <app-form-content-layout>
            <h1>Verify your identity</h1>
            <p>Select the account associated with your identity.</p>

            <p
                v-if="inlineError"
                class="select-account-view__error"
                role="alert"
                data-testid="inline-error">
                {{ inlineError }}
            </p>

            <app-radio
                v-for="row in matchedAccounts"
                :key="rowKey(row)"
                v-model="selectedKey"
                :native-value="rowKey(row)"
                :data-id="`account-${row.accountId}-user-${row.userId}`">
                <span class="select-account-view__primary">
                    {{ displayName(row) }}
                </span>
            </app-radio>
        </app-form-content-layout>

        <action-bar-layout>
            <app-button
                size="large"
                :is-loading="isSending"
                :disabled="!selectedRow"
                expanded
                @onClick="selectAccount">
                Continue
            </app-button>
            <app-button
                size="large"
                :is-loading="isLoadingNoMatchSelection"
                expanded
                type="secondary"
                @onClick="selectNoMatchedAccount">
                None of these are mine
            </app-button>
        </action-bar-layout>
    </flow-layout>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.select-account-view {
    &__primary {
        font-weight: 600;
    }

    &__error {
        color: $red;
        margin: 0.5rem 0;
    }
}
</style>
